import type { JsonObject, RenderNode, RenderTree } from '@inf-monkeys-tech/monkeys';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';

import { RenderTreeRuntimeHost } from '../runtime';
import type {
  RenderTreeRuntimeHostProps,
  RenderTreeNodeContext,
} from '../runtime';
import { CapabilityStatus } from './CapabilityStatus';
import type {
  CapabilityAccessDecision,
  CapabilityAccessRegistry,
  CapabilityBindingRegistry,
  CapabilityIntentHandler,
  CapabilityPerformanceEvidenceHandler,
  CapabilityProviderResult,
  CapabilityTranslate,
  CompiledDesignCapabilityRuntime,
  DesignCapabilityCatalogEntry,
  DesignCapabilityProvider,
  DesignCapabilityRenderer,
} from './contracts';
import { isJsonObject } from './contracts';

type ProviderState =
  | { kind: 'ready'; model: JsonObject }
  | { kind: 'loading'; model: JsonObject }
  | { kind: 'empty'; model: JsonObject }
  | { kind: 'error'; model: JsonObject; code: string };

export interface CapabilityRuntimeHostProps
  extends Omit<RenderTreeRuntimeHostProps, 'renderNode' | 'tree'> {
  tree: RenderTree;
  runtime: CompiledDesignCapabilityRuntime;
  translate: CapabilityTranslate;
  bindings?: CapabilityBindingRegistry;
  access?: CapabilityAccessRegistry;
  resolveBinding?: (
    node: RenderNode,
    bindings: CapabilityBindingRegistry,
  ) => unknown;
  resolveAccess?: (
    node: RenderNode,
    access: CapabilityAccessRegistry,
  ) => CapabilityAccessDecision;
  onIntent?: CapabilityIntentHandler;
  onPerformanceEvidence?: CapabilityPerformanceEvidenceHandler;
  now?: () => number;
  renderMissingCapability?: (
    context: RenderTreeNodeContext,
    error: unknown,
  ) => ReactNode;
}

interface CapabilityNodeBoundaryProps {
  context: RenderTreeNodeContext;
  entry: DesignCapabilityCatalogEntry;
  provider: DesignCapabilityProvider;
  renderer: DesignCapabilityRenderer;
  binding: unknown;
  access: CapabilityAccessDecision;
  translate: CapabilityTranslate;
  onIntent?: CapabilityIntentHandler;
  onPerformanceEvidence?: CapabilityPerformanceEvidenceHandler;
  now: () => number;
}

const providerStateFromNode = (node: RenderNode): ProviderState => {
  if (node.state === 'loading') return { kind: 'loading', model: node.renderModel };
  if (node.state === 'empty') return { kind: 'empty', model: node.renderModel };
  if (node.state === 'error') {
    return {
      kind: 'error',
      model: node.renderModel,
      code: 'capability.render.error',
    };
  }
  return { kind: 'ready', model: node.renderModel };
};

const errorCode = (error: unknown) => {
  if (
    typeof error === 'object'
    && error !== null
    && 'code' in error
    && typeof error.code === 'string'
    && error.code.length <= 128
    && /^[a-z0-9]+(?:[._-][a-z0-9]+)*$/i.test(error.code)
  ) {
    return error.code;
  }
  return 'capability.provider.failed';
};

const CapabilityNodeBoundary = ({
  context,
  entry,
  provider,
  renderer,
  binding,
  access,
  translate,
  onIntent,
  onPerformanceEvidence,
  now,
}: CapabilityNodeBoundaryProps) => {
  const { node } = context;
  const [attempt, setAttempt] = useState(0);
  const [providerState, setProviderState] = useState<ProviderState>(() =>
    providerStateFromNode(node));
  const generationRef = useRef(0);
  const performanceEvidenceRef = useRef(onPerformanceEvidence);

  useEffect(() => {
    performanceEvidenceRef.current = onPerformanceEvidence;
  }, [onPerformanceEvidence]);

  useEffect(() => {
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    if (!access.allowed || !context.queryEnabled || !provider.load) {
      setProviderState(providerStateFromNode(node));
      return undefined;
    }

    const controller = new AbortController();
    const startedAt = now();
    let settled = false;
    setProviderState({ kind: 'loading', model: node.renderModel });

    const report = (
      outcome: 'success' | 'empty' | 'error' | 'cancelled' | 'budget-exceeded',
      durationMs: number,
    ) => performanceEvidenceRef.current?.({
      capabilityId: entry.manifest.id,
      providerId: provider.providerId,
      nodeId: node.nodeId,
      durationMs,
      budgetMs: entry.provider.performance.budgetMs,
      outcome,
    });

    const fail = (error: unknown, durationMs: number) => {
      if (controller.signal.aborted || generationRef.current !== generation) return;
      settled = true;
      setProviderState({
        kind: 'error',
        model: node.renderModel,
        code: errorCode(error),
      });
      report('error', durationMs);
    };

    const finish = (
      result: CapabilityProviderResult,
      durationMs: number,
    ) => {
      if (controller.signal.aborted || generationRef.current !== generation) return;
      if (
        !isJsonObject(result?.model)
        || (result.empty !== undefined && typeof result.empty !== 'boolean')
      ) {
        fail({ code: 'capability.provider.invalid-result' }, durationMs);
        return;
      }
      settled = true;
      const budgetMs = entry.provider.performance.budgetMs;
      const outcome = budgetMs !== undefined && durationMs > budgetMs
        ? 'budget-exceeded'
        : result.empty
          ? 'empty'
          : 'success';
      setProviderState(
        result.empty
          ? { kind: 'empty', model: result.model }
          : { kind: 'ready', model: result.model },
      );
      report(outcome, durationMs);
    };

    let pending: Promise<CapabilityProviderResult>;
    try {
      pending = Promise.resolve(provider.load({
        node,
        manifest: entry.manifest,
        descriptor: entry.provider,
        binding,
        signal: controller.signal,
      }));
    } catch (error) {
      pending = Promise.reject(error);
    }

    pending.then(
      (result) => finish(result, Math.max(0, now() - startedAt)),
      (error) => fail(error, Math.max(0, now() - startedAt)),
    );

    return () => {
      controller.abort();
      if (!settled) {
        report('cancelled', Math.max(0, now() - startedAt));
      }
    };
  }, [
    access.allowed,
    attempt,
    binding,
    context.queryEnabled,
    entry,
    node,
    now,
    provider,
  ]);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);
  const emitIntent = useCallback((type: string, payload: JsonObject = {}) => {
    onIntent?.({ type, payload }, node);
  }, [node, onIntent]);

  if (!access.allowed) {
    return (
      <CapabilityStatus
        kind="forbidden"
        code={access.reasonCode ?? 'capability.access.forbidden'}
        translate={translate}
      />
    );
  }
  if (providerState.kind === 'loading') {
    return <CapabilityStatus kind="loading" translate={translate} />;
  }
  if (providerState.kind === 'empty') {
    return <CapabilityStatus kind="empty" translate={translate} />;
  }
  if (providerState.kind === 'error') {
    return (
      <CapabilityStatus
        kind="error"
        code={providerState.code}
        translate={translate}
        onRetry={provider.load ? retry : undefined}
      />
    );
  }

  return renderer({
    ...context,
    entry,
    binding,
    model: providerState.model,
    translate,
    access,
    loading: false,
    empty: false,
    retry,
    emitIntent,
  });
};

const defaultResolveBinding = (
  node: RenderNode,
  bindings: CapabilityBindingRegistry,
) => node.dataContextRef ? bindings[node.dataContextRef.id] : undefined;

const defaultResolveAccess = (
  node: RenderNode,
  access: CapabilityAccessRegistry,
): CapabilityAccessDecision => {
  if (!node.accessRef) return { allowed: true };
  return access[node.accessRef.id] ?? {
    allowed: false,
    reasonCode: 'capability.access.unresolved',
  };
};

const defaultNow = () => (
  typeof performance === 'undefined' ? Date.now() : performance.now()
);

export const CapabilityRuntimeHost = ({
  tree,
  runtime,
  translate,
  bindings = {},
  access = {},
  resolveBinding = defaultResolveBinding,
  resolveAccess = defaultResolveAccess,
  onIntent,
  onPerformanceEvidence,
  now = defaultNow,
  renderMissingCapability,
  elementForNode,
  ...props
}: CapabilityRuntimeHostProps) => {
  const renderNode = useMemo(() => (
    context: RenderTreeNodeContext,
  ) => {
    try {
      const registration = runtime.resolve(context.node, tree.product);
      return (
        <CapabilityNodeBoundary
          context={context}
          {...registration}
          binding={resolveBinding(context.node, bindings)}
          access={resolveAccess(context.node, access)}
          translate={translate}
          onIntent={onIntent}
          onPerformanceEvidence={onPerformanceEvidence}
          now={now}
        />
      );
    } catch (error) {
      return renderMissingCapability?.(context, error) ?? (
        <CapabilityStatus
          kind="error"
          code="capability.registration.missing"
          translate={translate}
        />
      );
    }
  }, [
    access,
    bindings,
    now,
    onIntent,
    onPerformanceEvidence,
    renderMissingCapability,
    resolveAccess,
    resolveBinding,
    runtime,
    translate,
    tree,
  ]);

  const resolvedElementForNode = useCallback((node: RenderNode) => {
    if (elementForNode) return elementForNode(node);
    if (node.kind === 'shell') return 'div';
    if (node.kind === 'navigation') return 'nav';
    if (node.kind === 'detail') return 'aside';
    if (node.kind === 'record') return 'article';
    return 'section';
  }, [elementForNode]);

  return (
    <RenderTreeRuntimeHost
      {...props}
      tree={tree}
      renderNode={renderNode}
      elementForNode={resolvedElementForNode}
    />
  );
};
